import { parseCSV } from './csv';
import { normalizeManagerName } from './managerStats';

function toNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function toYear(value) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) ? n : null;
}

function isPlayoffChampion(method) {
  return /Playoff Champion/i.test(method || '');
}

export function buildSeasonVault({ championships = [], standings = [], playoffs = [], matchups = [] }) {
  const years = Array.from(new Set([
    ...championships.map((row) => toYear(row.Year)).filter(Boolean),
    ...standings.map((row) => toYear(row.Year)).filter(Boolean),
    ...playoffs.map((row) => toYear(row.Year)).filter(Boolean),
    ...matchups.map((row) => toYear(row.Year || row.Season)).filter(Boolean),
  ])).sort((a, b) => b - a);

  return years.map((year) => {
    const championRow = championships.find((row) => toYear(row.Year) === year && isPlayoffChampion(row.Method));
    const standingsRows = standings
      .filter((row) => toYear(row.Year) === year)
      .map((row) => ({
        manager: normalizeManagerName(row.Manager),
        team: row.Team,
        wins: toNumber(row.Wins) || 0,
        losses: toNumber(row.Losses) || 0,
        pointsFor: toNumber(row['Points For']) || 0,
      }))
      .sort((a, b) => b.wins - a.wins || b.pointsFor - a.pointsFor);

    const yearMatchups = matchups.filter((row) => toYear(row.Year || row.Season) === year);
    const titleGame = titleGameFor(year, playoffs);

    return {
      year,
      champion: championRow ? normalizeManagerName(championRow.Champion) : null,
      championTeam: championRow?.Team || null,
      championRecord: championRow?.Record || null,
      regularSeason: standingsRows[0] || null,
      titleGame,
      highestScore: highestScoreFor(yearMatchups),
      closestGame: closestGameFor(yearMatchups),
      teams: standingsRows.length,
    };
  });
}

export async function loadSeasonVault(fetchImpl = fetch) {
  const [championships, standings, playoffs, matchups] = await Promise.all([
    loadCsv('/data/championships.csv', fetchImpl),
    loadCsv('/data/season_standings.csv', fetchImpl),
    loadCsv('/data/playoff_data.csv', fetchImpl),
    loadCsv('/data/weekly_matchups.csv', fetchImpl),
  ]);

  return buildSeasonVault({ championships, standings, playoffs, matchups });
}

async function loadCsv(path, fetchImpl) {
  const response = await fetchImpl(path);
  if (!response.ok) throw new Error(`Failed to load ${path}`);
  return parseCSV(await response.text());
}

function titleGameFor(year, playoffs) {
  const row = playoffs.find((game) => (
    toYear(game.Year) === year
    && (game.Type === 'Championship' || game.Round === 'Championship')
  ));
  if (!row) return null;

  const usesRoundColumn = row.Round === 'Championship';
  const team1Manager = normalizeManagerName(usesRoundColumn ? row['Team1 Manager'] : row.Round);
  const team1Name = usesRoundColumn ? row['Team1 Name'] : row['Team1 Manager'];
  const team1Score = toNumber(usesRoundColumn ? row['Team1 Score'] : row['Team1 Name']);
  const team2Manager = normalizeManagerName(usesRoundColumn ? row['Team2 Manager'] : row['Team1 Score']);
  const team2Name = usesRoundColumn ? row['Team2 Name'] : row['Team2 Manager'];
  const team2Score = toNumber(usesRoundColumn ? row['Team2 Score'] : row['Team2 Name']);

  if (team1Score === null || team2Score === null) return null;

  const team1Won = team1Score >= team2Score;
  return {
    winner: team1Won ? team1Manager : team2Manager,
    runnerUp: team1Won ? team2Manager : team1Manager,
    winnerTeam: team1Won ? team1Name : team2Name,
    runnerUpTeam: team1Won ? team2Name : team1Name,
    winnerScore: team1Won ? team1Score : team2Score,
    runnerUpScore: team1Won ? team2Score : team1Score,
    margin: Math.abs(team1Score - team2Score),
  };
}

function highestScoreFor(matchups) {
  const scores = [];
  matchups.forEach((row) => {
    const year = toYear(row.Year || row.Season);
    const week = toNumber(row.Week);
    [
      ['Team1 Manager', 'Team1 Name', 'Team1 Score'],
      ['Team2 Manager', 'Team2 Name', 'Team2 Score'],
    ].forEach(([managerKey, teamKey, scoreKey]) => {
      const score = toNumber(row[scoreKey]);
      if (score !== null) {
        scores.push({
          year,
          week,
          manager: normalizeManagerName(row[managerKey]),
          team: row[teamKey],
          score,
        });
      }
    });
  });

  return scores.sort((a, b) => b.score - a.score)[0] || null;
}

function closestGameFor(matchups) {
  const games = matchups
    .map((row) => {
      const team1Score = toNumber(row['Team1 Score']);
      const team2Score = toNumber(row['Team2 Score']);
      if (team1Score === null || team2Score === null) return null;

      const team1Won = team1Score >= team2Score;
      return {
        week: toNumber(row.Week),
        winner: team1Won ? normalizeManagerName(row['Team1 Manager']) : normalizeManagerName(row['Team2 Manager']),
        loser: team1Won ? normalizeManagerName(row['Team2 Manager']) : normalizeManagerName(row['Team1 Manager']),
        winnerScore: team1Won ? team1Score : team2Score,
        loserScore: team1Won ? team2Score : team1Score,
        margin: Math.abs(team1Score - team2Score),
      };
    })
    .filter(Boolean);

  return games.sort((a, b) => a.margin - b.margin)[0] || null;
}

export function formatScore(value) {
  if (!Number.isFinite(value)) return '-';
  return value.toFixed(2);
}
